import { Checkbox, NumberInput, Select, SimpleGrid, Stack, Text } from '@mantine/core';
import { schemaResolver, useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { IconHandClick, IconRobot, IconSunrise, IconSunset2 } from '@tabler/icons-react';
import { Panel } from 'components/Panel';
import { SaveButton } from 'components/SaveButton';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect } from 'react';
import { pick } from 'remeda';
import HTML from 'shared/components/HTML/HTML';
import type { z } from 'zod/v4';

import { type CardOption, OptionCardGroup } from '../../components';
import { type LightTemplate, type StepType, useLoadTemplates } from '../../Templates/api';
import { RecoverySettingsSchema, useLoadRecoverySettings, useUpdateRecoverySettings } from '../api';

const RecoveryPreferencesSchema = RecoverySettingsSchema.pick({
  automaticReminders: true,
  daysBetweenReminder: true,
  defaultAdditionalReminderTemplateId: true,
  defaultFormalNoticeTemplateId: true,
  reminderTimeSlot: true,
  sendRemindersForImportedInvoices: true,
});

type RecoveryPreferences = z.infer<typeof RecoveryPreferencesSchema>;

const RECOVERY_PREFERENCES_FIELDS = RecoveryPreferencesSchema.keyof().options;

const INITIAL_VALUES: RecoveryPreferences = {
  automaticReminders: true,
  daysBetweenReminder: 6,
  defaultAdditionalReminderTemplateId: null,
  defaultFormalNoticeTemplateId: null,
  reminderTimeSlot: 'morning',
  sendRemindersForImportedInvoices: false,
};

type ReminderMode = 'automatic' | 'manual';

const REMINDER_MODE_OPTIONS: CardOption<ReminderMode>[] = [
  { value: 'automatic', labelKey: i18nKeys.FORM.PREFERENCES.AUTOMATIC, Icon: IconRobot },
  { value: 'manual', labelKey: i18nKeys.FORM.PREFERENCES.MANUAL, Icon: IconHandClick },
];

const TIME_SLOT_OPTIONS: CardOption<RecoveryPreferences['reminderTimeSlot']>[] = [
  { value: 'morning', labelKey: i18nKeys.MORNING, Icon: IconSunrise },
  { value: 'afternoon', labelKey: i18nKeys.AFTERNOON, Icon: IconSunset2 },
];

const toTemplateOptions = (templates: LightTemplate[], stepType: StepType) =>
  templates
    .filter(template => template.stepType === stepType)
    .map(template => ({ value: template.id, label: template.name }));

export const RecoverySettingsCard = () => {
  const { t } = useTranslation();

  const { recoverySettings } = useLoadRecoverySettings();
  const { templates } = useLoadTemplates({ templateType: 'recovery' });
  const { updateRecoverySettings, isUpdateRecoverySettingsLoading } = useUpdateRecoverySettings();

  const form = useForm<RecoveryPreferences>({
    initialValues: INITIAL_VALUES,
    validate: schemaResolver(RecoveryPreferencesSchema, { sync: true }),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: resync only when this card's own fields change on the server, so saving the sibling reminder thresholds card does not wipe unsaved edits here
  useEffect(() => {
    if (!recoverySettings) return;

    const preferences = RecoveryPreferencesSchema.parse(recoverySettings);
    form.setInitialValues(preferences);
    form.setValues(preferences);
  }, [
    recoverySettings?.automaticReminders,
    recoverySettings?.daysBetweenReminder,
    recoverySettings?.defaultAdditionalReminderTemplateId,
    recoverySettings?.defaultFormalNoticeTemplateId,
    recoverySettings?.reminderTimeSlot,
    recoverySettings?.sendRemindersForImportedInvoices,
  ]);

  const save = () => {
    const dirtyFields = RECOVERY_PREFERENCES_FIELDS.filter(field => form.isDirty(field));

    updateRecoverySettings(pick(form.getValues(), dirtyFields), {
      onSuccess: () => form.resetDirty(),
    });
  };

  const handleSave = () => {
    if (form.validate().hasErrors) return;
    if (!form.isDirty('automaticReminders')) return save();

    modals.openConfirmModal({
      title: t(i18nKeys.CONFIRMATION),
      children: (
        <Text size="sm" component="div">
          <HTML
            html={t(
              form.getValues().automaticReminders
                ? i18nKeys.FORM.PREFERENCES.AYS_AUTOMATIC_REMINDER_MODE
                : i18nKeys.FORM.PREFERENCES.AYS_MANUAL_REMINDER_MODE,
            )}
          />
        </Text>
      ),
      labels: { confirm: t(i18nKeys.CONFIRM), cancel: t(i18nKeys.CANCEL) },
      onConfirm: save,
    });
  };

  const { automaticReminders, reminderTimeSlot } = form.getValues();

  return (
    <Panel
      title={t(i18nKeys.FORM.PREFERENCES.RECOVERY_SETTINGS)}
      withBorder
      footer={
        <SaveButton
          onClick={handleSave}
          disabled={!form.isDirty()}
          loading={isUpdateRecoverySettingsLoading}
          hideIcon
        />
      }
    >
      <Stack my="md" gap="lg">
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl" verticalSpacing="lg">
          <Stack gap="lg">
            <Select
              label={t(i18nKeys.DEFAULT_FORMAL_NOTICE)}
              data={toTemplateOptions(templates, 'formal_notice')}
              allowDeselect={false}
              searchable
              {...form.getInputProps('defaultFormalNoticeTemplateId')}
            />
            <Select
              label={t(i18nKeys.DEFAULT_ADDITIONAL_REMINDER)}
              data={toTemplateOptions(templates, 'additional_reminder')}
              allowDeselect={false}
              searchable
              {...form.getInputProps('defaultAdditionalReminderTemplateId')}
            />
            <NumberInput
              label={t(i18nKeys.FORM.PREFERENCES.DAYS_BETWEEN_TWO_REMINDERS)}
              min={0}
              allowNegative={false}
              allowDecimal={false}
              hideControls
              {...form.getInputProps('daysBetweenReminder')}
            />
          </Stack>
          <Stack gap="lg">
            <OptionCardGroup
              label={t(i18nKeys.FORM.PREFERENCES.REMINDER_MANAGEMENT)}
              value={automaticReminders ? 'automatic' : 'manual'}
              options={REMINDER_MODE_OPTIONS}
              onChange={next => form.setFieldValue('automaticReminders', next === 'automatic')}
            />
            <OptionCardGroup
              label={t(i18nKeys.FORM.PREFERENCES.REMINDER_TIME_SLOT)}
              value={reminderTimeSlot}
              options={TIME_SLOT_OPTIONS}
              onChange={next => form.setFieldValue('reminderTimeSlot', next)}
            />
          </Stack>
        </SimpleGrid>
        <Checkbox
          label={t(i18nKeys.FORM.PREFERENCES.SEND_AUTOMATIC_REMINDERS)}
          {...form.getInputProps('sendRemindersForImportedInvoices', { type: 'checkbox' })}
        />
      </Stack>
    </Panel>
  );
};
